import { AxiosResponse } from 'axios'
import { chowlyApi } from 'config'
import { generatePath } from 'react-router-dom'

import chowlyApiClient from '../chowly'

export interface ConnectionStatus {
  id: string
  type: 'connection_status'
  attributes: {
    connection_type: 'point_of_sale' | 'order_platform'
    connection_status: 'CONNECTED' | 'DISCONNECTED' | 'ERROR'
    operational_state: 'OPERATIONAL' | 'NON_OPERATIONAL' | 'UNKNOWN'
    operational_state_last_checked: string
    details?: {
      coo_url?: string
      website_url?: string
    }
  }
  relationships?: {
    order_platform?: {
      data: {
        id: string
        type: 'order_platform'
      }
    }
    point_of_sale?: {
      data: {
        id: string
        type: 'point_of_sale'
      }
    }
    partner?: {
      data: {
        id: string
        type: 'partner'
      }
    }
  }
  included?: Array<{
    id: string
    type: 'order_platform' | 'point_of_sale' | 'partner'
    attributes: any
  }>
}

export interface ConnectionStatusResponse {
  data: ConnectionStatus[]
  included?: Array<{
    id: string
    type: 'point_of_sale' | 'order_platform' | 'partner'
    attributes: any
  }>
}

export interface SingleConnectionStatusResponse {
  data: ConnectionStatus
  included?: Array<{
    id: string
    type: 'point_of_sale' | 'order_platform' | 'partner'
    attributes: any
  }>
}

// Get All Connection Statuses for a Partner (both POS and TOOS)
export const getAllConnectionStatuses = async (
  partnerId: string
): Promise<ConnectionStatusResponse> => {
  const url = generatePath(chowlyApi.connectionStatuses, { partnerId })
  // Include both point_of_sale and order_platform in a single request
  const response: AxiosResponse<ConnectionStatusResponse> = await chowlyApiClient.get(
    `${url}?include=point_of_sale,order_platform`
  )
  return response.data
}

// Get Specific Connection Status
export const getConnectionStatus = async (
  partnerId: string,
  connectionStatusId: string,
  include?: string
): Promise<SingleConnectionStatusResponse> => {
  const url = generatePath(chowlyApi.connectionStatus, { partnerId, id: connectionStatusId })
  const includeParam = include ? `?include=${include}` : '?include=point_of_sale,order_platform'
  const response: AxiosResponse<SingleConnectionStatusResponse> = await chowlyApiClient.get(
    `${url}${includeParam}`
  )
  return response.data
}

// Refresh Connection Status (User-Triggered)
export const refreshConnectionStatus = async (
  partnerId: string,
  connectionStatusId: string,
  include?: string
): Promise<SingleConnectionStatusResponse> => {
  const url = generatePath(chowlyApi.connectionStatusRefresh, { partnerId, id: connectionStatusId })
  const includeParam = include ? `?include=${include}` : '?include=point_of_sale,order_platform'
  const response: AxiosResponse<SingleConnectionStatusResponse> = await chowlyApiClient.post(
    `${url}${includeParam}`
  )
  return response.data
}

// Helper functions to filter connections by type
export const filterConnectionsByType = (
  connections: ConnectionStatus[], 
  connectionType: string
): ConnectionStatus[] => {
  return connections.filter(connection => 
    connection.attributes.connection_type === connectionType
  )
}